<script setup>
import { BTable } from 'bootstrap-vue-next';
import { ref } from 'vue';

import RoutePagination from '~/common/components/RoutePagination.vue';
import ThemedOverlay from '~/common/components/ThemedOverlay.vue';
import UserWithGitLabProfileLink from '~/common/components/UserWithGitLabProfileLink.vue';
import { dateFieldFormatter } from '~/common/FieldFormats';

const props = defineProps({
  records: {
    type: Array,
    default: () => [],
  },
  metadata: {
    type: Object,
    default: () => ({}),
  },
  busy: {
    type: Boolean,
    default: false,
  },
  initialSortBy: {
    type: String,
    default: 'merged_date',
  },
  initialSortDirection: {
    type: String,
    default: 'desc',
  },
});

const emit = defineEmits(['sort']);

const page = defineModel('page', { type: Number, default: 1 });

const sortBy = ref([
  { key: props.initialSortBy, order: props.initialSortDirection },
]);

const stripPrefix = ({ value }) => String(value ?? '').replace(/^(group|section|devops)::/, '');

const fields = [
  { key: 'title', label: 'Title', sortable: true },
  { key: 'username', label: 'Author', sortable: true, tdClass: 'text-nowrap' },
  { key: 'merged_date', label: 'Merged', sortable: true, formatter: dateFieldFormatter, tdClass: 'text-nowrap' },
  { key: 'section_label', label: 'Section', sortable: true, formatter: stripPrefix },
  { key: 'stage_label', label: 'Stage', sortable: true, formatter: stripPrefix },
  { key: 'group_label', label: 'Group', sortable: true, formatter: stripPrefix },
];

const onSortUpdate = (newSortBy) => {
  sortBy.value = newSortBy;
  const current = newSortBy[0] || { key: 'merged_date', order: 'desc' };
  page.value = 1;
  emit('sort', { sortBy: current.key, sortDirection: current.order });
};
</script>

<template>
  <ThemedOverlay :show="busy">
    <BTable
      :sort-by="sortBy"
      :items="records"
      :fields="fields"
      no-local-sorting
      striped
      hover
      responsive
      show-empty
      empty-text="No merge requests found for the selected filters."
      @update:sort-by="onSortUpdate"
    >
      <template #cell(title)="{ item }">
        <a
          :href="item.web_url"
          target="_blank"
        >
          {{ item.title }}
        </a>
      </template>
      <template #cell(username)="{ value }">
        <UserWithGitLabProfileLink
          v-if="value"
          :username="value"
        />
      </template>
    </BTable>

    <RoutePagination
      v-if="metadata.total_count"
      v-model="page"
      :total-rows="metadata.total_count"
      :per-page="metadata.per_page || 20"
    />
  </ThemedOverlay>
</template>
